import { Dropzone, FileMosaic } from "@dropzone-ui/react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useEffect, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import DefaultApiService from "../../../../infrastructure/api/default";
import { openUploadedFile } from "../../../../infrastructure/utils/uploadedFileAccess";
import { resolveUploadUrl } from "../../../../infrastructure/utils/resolveUploadUrl";

const FileUploader = ({
  moduleName,
  accept,
  maxFiles,
  label,
  onChange,
  placeholder,
  fullPath = false,
  baseUrl = "",
  ...params
}) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false);

  // همیشه با تغییر value، فایل(های) موجود را نمایش بده (مخصوصاً وقتی داده به‌صورت async لود می‌شود)
  useEffect(() => {
    let fileValues = params?.value;
    if (!Array.isArray(fileValues)) {
      fileValues = fileValues && typeof fileValues === "string" ? [fileValues] : [];
    }
    const validUrls = fileValues.filter((v) => v && typeof v === "string");
    if (validUrls.length > 0) {
      setFiles(
        validUrls.map((fileValue, idx) => {
          const previewLink = resolveUploadUrl(fileValue, baseUrl);
          return {
            id: idx + 1,
            name: fileValue.split("/").pop() || fileValue,
            size: 13781,
            type: accept,
            valid: true,
            fileId: idx + 11,
            fileGuid: fileValue,
            directory: fileValue,
            previewLink,
            extension: params.extension,
          };
        })
      );
    } else {
      setFiles([]);
    }
  }, [params?.value, accept, baseUrl, params.extension]);

  const updateFiles = async (incomingFiles) => {
    try {
      if (incomingFiles.length > 0) {
        setLoading(true);

        const result = await DefaultApiService.uploadFile(
          incomingFiles,
          moduleName
        );
        if (result.isSuccess) {
          if (result?.data?.length) {
            for (let i = 0; i < result.data.length; i++) {
              const data = result.data[i];
              incomingFiles[i] = {
                ...incomingFiles[i],
                ...data,
              };
            }
          }
          onMakeValueData(incomingFiles);
        } else {
          toast.error(result.message);
        }
      }
      setFiles(incomingFiles);

      setLoading(false);
    } catch (error) {
      console.error("error", error);
      setLoading(false);

      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    }
  };

  const onRemoveFile = (fileIndex, event) => {
    event.preventDefault();
    event.stopPropagation();

    const newFiles = [...files];
    newFiles.splice(fileIndex, 1);
    setFiles(newFiles);
    onMakeValueData(newFiles);
  };

  const onMakeValueData = (list) => {
    if (onChange) {
      if (list.length === 1) {
        onChange(list[0].directory);
      } else if (list.length > 1) {
        onChange(list.map((x) => x.directory));
      } else {
        onChange("");
      }
    }
  };

  const handleOpenStoredFile = async (file) => {
    const path = file?.directory || file?.fileGuid || "";
    if (!path) return;
    if (file?.file instanceof File) {
      const localUrl = URL.createObjectURL(file.file);
      window.open(localUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(localUrl), 60000);
      return;
    }
    setOpening(true);
    try {
      await openUploadedFile(path, baseUrl, {
        fileName: file?.name || path.split("/").pop() || "file",
      });
    } catch (err) {
      toast.error(err?.message || "باز کردن فایل ناموفق بود.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12} md={12} sx={{ mb: 0.5 }}>
        <Typography>{label}</Typography>
        <TextField {...params} disabled sx={{ display: "none" }} />
      </Grid>

      <Grid item xs={12} md={12}>
        <Dropzone
          onChange={updateFiles}
          value={files}
          multiple={false}
          maxFiles={maxFiles}
          style={{ direction: "ltr" }}
          accept={accept}
        >
          {files.length > 0 &&
            files.map((file, index) => (
              <div key={index} style={{ position: "relative" }}>
                <HighlightOffIcon
                  sx={{
                    position: "absolute",
                    left: 6,
                    zIndex: 10,
                  }}
                  color="secondary"
                  onClick={(e) => onRemoveFile(index, e)}
                />
                {file.file ? (
                  <FileMosaic {...file} preview />
                ) : accept === "image/*" ||
                  /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(
                    file.name || file.directory || ""
                  ) ? (
                  <>
                    <img
                      src={file.previewLink}
                      style={{ height: 120, maxWidth: "100%", objectFit: "contain" }}
                      alt={file.name || "preview"}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                      disabled={opening}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenStoredFile(file);
                      }}
                    >
                      مشاهده فایل
                    </Button>
                  </>
                ) : (
                  <Box>
                    <Button
                      size="large"
                      color="primary"
                      variant="outlined"
                      disabled={opening}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenStoredFile(file);
                      }}
                    >
                      <Typography
                        sx={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          maxWidth: 200,
                          textOverflow: "ellipsis",
                        }}
                        variant="caption"
                      >
                        {file.name}
                      </Typography>
                    </Button>
                  </Box>
                )}
              </div>
            ))}

          {loading && (
            <CircularProgress color="primary" size={80} thickness={5} />
          )}
        </Dropzone>
        {params.error && (
          <FormHelperText error={params.error}>
            {params.helperText}
          </FormHelperText>
        )}
      </Grid>
    </Grid>
  );
};

export default FileUploader;
